import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * Desktop hover still opens submenus. Keyboard users tab across top-level items
 * without entering collapsed mega menus. Enter or Space on a parent label (or
 * its caret) toggles that submenu; Escape closes it. Collapsed panels use
 * `inert` so they stay out of the tab order.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {number} */
  #deactivateDelay = 200;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #deactivateTimeout = null;

  /**
   * When true, the next pointing-device click on an already-open control is the
   * same gesture that opened it (hover or the tap's pointerenter). Ignore that
   * click so the panel does not open and shut at once.
   * @type {boolean}
   */
  #suppressPointerToggle = false;

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerenter', this.#cancelDeactivate, true);
    this.addEventListener('click', this.#onParentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerenter', this.#cancelDeactivate, true);
    this.removeEventListener('click', this.#onParentClick);
    this.#cancelDeactivate();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close the open submenu on Escape, or toggle a parent item with Enter/Space.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      const item = this.#state.activeItem;
      if (!item) return;

      const control = this.#focusTargetAfterClose(item);
      this.#deactivate(item, {force: true});
      control.focus();
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active.tagName !== 'A') return;
    if (active.closest('.menu-list__submenu')) return;

    const item = findMenuItem(active);
    if (item !== active || !isSubmenuTrigger(item)) return;

    event.preventDefault();
    this.toggle(event);
  };

  /**
   * Toggle a submenu from its parent label, caret, or the More trigger.
   * Keyboard Enter/Space still opens and closes. A pointing-device click must
   * not close a panel that hover (or the same tap's pointerenter) just opened.
   * @param {PointerEvent | MouseEvent | KeyboardEvent} event
   */
  toggle = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const item = findMenuItem(target);
    if (!item) return;

    const isExpanded = item.getAttribute('aria-expanded') === 'true';
    const isOverflowItem = Boolean(item.closest('[slot="overflow"]'));

    if (isExpanded) {
      if (isPointerGeneratedClick(event) && this.#suppressPointerToggle) {
        this.#suppressPointerToggle = false;
        return;
      }

      this.#suppressPointerToggle = false;

      if (isOverflowItem) {
        this.#collapseOverflowChild(item);
        return;
      }

      this.#deactivate(this.#state.activeItem ?? item, {force: true});
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== item) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';

    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }

    return item;
  }

  /**
   * Prevents navigation on parent links that have a submenu when the merchant
   * disabled parent URLs, and on nested column parents marked `data-disable-link`.
   * @param {MouseEvent} event
   */
  #onParentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const nestedParentLink = target.closest('a[data-disable-link]');

    if (nestedParentLink) {
      event.preventDefault();
      return;
    }

    if (!this.hasAttribute('data-disable-parent-link')) return;

    const link = target.closest('a[ref="menuitem"]');
    if (!link || !isSubmenuTrigger(link)) return;

    event.preventDefault();
    this.toggle(event);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | MouseEvent | KeyboardEvent} event
   */
  activate = (event) => {
    this.#cancelDeactivate();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';

    // Leaf items have no submenu. Close any open mega menu and leave the link
    // as a normal navigation target (cursor: pointer, clickable href).
    if (!isSubmenuTrigger(item)) {
      if (this.#state.activeItem) {
        this.#deactivate(this.#state.activeItem, {force: true});
      }
      return;
    }

    this.#suppressPointerToggle = event.type === 'pointerenter';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;
    const previousIsMore = Boolean(previouslyActiveItem?.closest('[slot="more"]'));

    if (previouslyActiveItem && !previousIsMore) {
      this.#collapseTrigger(previouslyActiveItem);
    }

    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== item) {
      this.#collapseTrigger(previouslyActiveOverflowItem);
    }

    if (isDefaultSlot) {
      this.#collapseOverflowPanel();
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = !isDefaultSlot && !isMoreTrigger ? item : null;
    this.ariaExpanded = 'true';
    if (item.hasAttribute('aria-controls') || isMoreTrigger) {
      item.ariaExpanded = 'true';
    }

    let submenu = findSubmenu(item) || (isMoreTrigger ? this.overflowMenu : null);
    const hasSubmenu = Boolean(findSubmenu(item));

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const activeItem = this.#state.activeItem;
    const listItem = activeItem?.closest('.menu-list__list-item');
    const menu = findSubmenu(activeItem);
    const isMovingWithinItem =
      event.relatedTarget instanceof Node && Boolean(listItem?.contains(event.relatedTarget));
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    const isMovingToActiveItem =
      event.relatedTarget instanceof Node && Boolean(activeItem?.contains(event.relatedTarget));
    const isMovingToOpenSubmenu = event.relatedTarget instanceof Node && Boolean(menu?.contains(event.relatedTarget));

    if (
      isMovingWithinItem ||
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu ||
      isMovingToActiveItem ||
      isMovingToOpenSubmenu
    ) {
      return;
    }

    this.#cancelDeactivate();
    this.#deactivateTimeout = setTimeout(() => {
      this.#deactivateTimeout = null;

      const item = this.#state.activeItem;
      const submenu = findSubmenu(item);

      if (item?.matches(':hover') || submenu?.matches(':hover')) return;
      if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

      this.#deactivate();
    }, this.#deactivateDelay);
  }

  #cancelDeactivate = () => {
    if (this.#deactivateTimeout) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, {force = false} = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle).
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#cancelDeactivate();
    this.#suppressPointerToggle = false;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const activeOverflowItem = this.#state.activeOverflowItem;

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#collapseTrigger(item);
    this.#collapseOverflowPanel();

    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#collapseTrigger(activeOverflowItem);
    }
  };

  /**
   * Close an overflow child's submenu without closing the More panel.
   * @param {HTMLElement} item
   */
  #collapseOverflowChild(item) {
    this.#collapseTrigger(item);

    if (this.#state.activeOverflowItem === item) {
      this.#state.activeOverflowItem = null;
    }

    if (this.#state.activeItem === item) {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) {
        this.#state.activeItem = moreButton;
        moreButton.ariaExpanded = 'true';
      }
    }

    const overflowListHeight = this.#getOverflowListLinksHeight();
    this.headerComponent?.style.setProperty('--submenu-height', `${overflowListHeight}px`);
    this.#setFullOpenHeaderHeight(overflowListHeight);
  }

  /**
   * Collapse a disclosure control and its submenu. Does not touch the overflow
   * panel; call `#collapseOverflowPanel` when leaving overflow for a top-level
   * item or closing the menu entirely.
   * @param {HTMLElement} item
   */
  #collapseTrigger(item) {
    if (isSubmenuTrigger(item) || item.hasAttribute('aria-expanded')) {
      item.ariaExpanded = 'false';
    }

    const submenu = findSubmenu(item);
    if (!submenu) return;

    delete submenu.dataset.active;
    submenu.inert = true;
  }

  /**
   * Reset the More control and overflow panel. More has no `aria-controls`, so
   * `activate` used to skip it and leave the panel non-`inert`.
   */
  #collapseOverflowPanel() {
    const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
    if (moreButton instanceof HTMLElement) {
      moreButton.ariaExpanded = 'false';
    }

    if (!this.overflowMenu) return;

    delete this.overflowMenu.dataset.active;
    this.overflowMenu.inert = true;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      for (const link of slottedMenuLinks) {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      }
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item') ?? element;
  const menuItem = listItem.querySelector('[ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Whether this top-level control discloses a submenu (caret items and More).
 * Leaf links are normal navigation and must not be treated as disclosures.
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isSubmenuTrigger(element) {
  if (!(element instanceof HTMLElement)) return false;

  return element.hasAttribute('aria-controls') || Boolean(element.closest('[slot="more"]'));
}

/**
 * True for a pointing-device click (mouse, touch, pen). Keyboard-generated
 * activation is a KeyboardEvent or a click with `detail === 0`.
 * @param {Event} event
 * @returns {boolean}
 */
function isPointerGeneratedClick(event) {
  if (event instanceof KeyboardEvent) return false;

  return 'detail' in event && event.detail !== 0;
}
